"use client";

import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { addDays, format as jFormat, startOfToday } from "date-fns-jalali";
import useSWR from "swr";
import HTMLContent from "@/containers/components/htmlContent/HTMLContent";
import { SearchLoadingExperience } from "@/shared/components/SearchLoadingExperience";
import { getCampaign } from "@/containers/website/campaign/campaign-registry";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { getHotelCityNameByCode } from "@/shared/constants/hotelCities";
import {
  HOTEL_RESULTS_V2_DEFAULT_FILTERS,
  type HotelResultsV2AmenityItem,
  type HotelResultsV2Filters,
  type HotelResultsV2Item,
  type HotelResultsV2OfferItem,
} from "./adapters/hotelResultsV2.model";
import { HOTEL_RESULTS_V2_SORT_OPTIONS } from "./constants";
import FilterSidebar from "./components/FilterSidebar";
import HotelCard from "./components/HotelCard";
import QuickFilters, { SecondaryQuickFilters } from "./components/QuickFilters";
import ResultsHero from "./components/ResultsHero";
import SearchSummary from "./components/SearchSummary";
import SortBar from "./components/SortBar";
import type {
  HotelResultsV2QuickFilterKey,
  HotelResultsV2SortValue,
  HotelResultsV2ViewMode,
} from "./types";
import { parseHotelResultsV2Date, toHotelResultsV2FaNumber } from "./utils";

const RESULTS_PAGE_SIZE = 8;
const FILTER_SIDEBAR_STICKY_TOP = 88;

interface HotelResultsV2ContainerProps {
  cityCode?: string;
  initialHotelResults?: {
    city: string;
    checkIn: string;
    checkOut: string;
    rooms: string;
    data: HotelResultsV2ApiResponse;
  } | null;
  initialStar?: number;
  seoTitle?: string;
  seoHtml?: TrustedHTML;
}

export default function HotelResultsV2Container({
  cityCode: routeCityCode,
  initialHotelResults,
  initialStar,
  seoTitle,
  seoHtml,
}: HotelResultsV2ContainerProps = {}) {
  const searchParams = useSearchParams();
  const cityCode = routeCityCode || searchParams.get("city") || "KIH";
  const cityName =
    searchParams.get("name") || getHotelCityNameByCode(cityCode) || cityCode;
  const checkIn = searchParams.get("checkIn") || getDefaultCheckIn();
  const checkOut = searchParams.get("checkOut") || getDefaultCheckOut();
  const roomsParam = searchParams.get("rooms") || "2";
  const initialData =
    initialHotelResults?.city === cityCode &&
    initialHotelResults.checkIn === checkIn &&
    initialHotelResults.checkOut === checkOut &&
    initialHotelResults.rooms === roomsParam
      ? initialHotelResults.data
      : undefined;
  const occupancySummary = getOccupancySummaryFromRoomsParam(roomsParam);
  const campaign = getCampaign("partner");
  const campaignHref = campaign ? `/c/${campaign.slug}` : undefined;
  const cityPreviewConfig = useMemo(
    () => getCityPreviewConfig(cityCode, cityName),
    [cityCode, cityName],
  );
  const nights = getNightsFromSearchParams(checkIn, checkOut);
  const staySummaryLabel = getStaySummaryLabel(nights, occupancySummary.guests);
  const initialFilters = useMemo(
    () => ({
      ...HOTEL_RESULTS_V2_DEFAULT_FILTERS,
      stars: isValidHotelStar(initialStar) ? [initialStar] : [],
    }),
    [initialStar],
  );
  const [filters, setFilters] = useState<HotelResultsV2Filters>(initialFilters);
  const [sort, setSort] = useState<HotelResultsV2SortValue>("recommended");
  const [viewMode, setViewMode] = useState<HotelResultsV2ViewMode>("list");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [activeQuick, setActiveQuick] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(RESULTS_PAGE_SIZE);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  const filterSidebarRef = useRef<HTMLDivElement>(null);
  const hasAppliedFiltersOnceRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const [filterSidebarHeight, setFilterSidebarHeight] = useState(0);
  const [filterSidebarOffset, setFilterSidebarOffset] = useState(0);
  const debouncedFilters = useDebouncedValue(filters, 180);
  const debouncedFeaturedOnly = useDebouncedValue(featuredOnly, 180);

  useEffect(() => {
    setFilters(initialFilters);
    setVisibleCount(RESULTS_PAGE_SIZE);
  }, [initialFilters]);

  const hotelResultsUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("city", cityCode);
    params.set("checkIn", checkIn);
    params.set("checkOut", checkOut);
    params.set("rooms", roomsParam);

    return `/api/hotel/results-v2/?${params.toString()}`;
  }, [checkIn, checkOut, cityCode, roomsParam, searchParams]);

  const {
    data: hotelResultsResponse,
    error: hotelResultsError,
    isLoading: isApiLoading,
  } = useSWR<HotelResultsV2ApiResponse>(hotelResultsUrl, fetchHotelResultsV2, {
    fallbackData: initialData,
    revalidateOnMount: initialData === undefined,
    revalidateOnFocus: false,
  });
  const rawHotels = useMemo(() => {
    if (!hotelResultsResponse) return [];

    return hotelResultsResponse.results ?? [];
  }, [hotelResultsResponse]);
  const sourceHotels = useMemo(
    () => rawHotels.filter((hotel) => hotel.isAvailable !== false),
    [rawHotels],
  );
  const sourcePriceRange = useMemo(() => {
    const prices = sourceHotels
      .map((hotel) => hotel.pricePerNight)
      .filter(
        (price): price is number =>
          typeof price === "number" && Number.isFinite(price) && price > 0,
      );

    return {
      min: prices.length
        ? Math.min(...prices)
        : HOTEL_RESULTS_V2_DEFAULT_FILTERS.minPrice,
      max: prices.length
        ? Math.max(...prices)
        : HOTEL_RESULTS_V2_DEFAULT_FILTERS.maxPrice,
    };
  }, [sourceHotels]);
  const amenityOptions = useMemo(
    () => getAmenityOptions(sourceHotels),
    [sourceHotels],
  );
  const offerOptions = useMemo(
    () => getOfferOptions(sourceHotels),
    [sourceHotels],
  );

  const activeFilters = useMemo(
    () => ({
      ...filters,
      minPrice:
        filters.minPrice === HOTEL_RESULTS_V2_DEFAULT_FILTERS.minPrice
          ? sourcePriceRange.min
          : filters.minPrice,
      maxPrice:
        filters.maxPrice === HOTEL_RESULTS_V2_DEFAULT_FILTERS.maxPrice
          ? sourcePriceRange.max
          : filters.maxPrice,
      areas: filters.areas.filter((area) =>
        cityPreviewConfig.areas.includes(area),
      ),
    }),
    [cityPreviewConfig.areas, filters, sourcePriceRange],
  );

  const appliedFilters = useMemo(
    () => ({
      ...debouncedFilters,
      minPrice:
        debouncedFilters.minPrice === HOTEL_RESULTS_V2_DEFAULT_FILTERS.minPrice
          ? sourcePriceRange.min
          : debouncedFilters.minPrice,
      maxPrice:
        debouncedFilters.maxPrice === HOTEL_RESULTS_V2_DEFAULT_FILTERS.maxPrice
          ? sourcePriceRange.max
          : debouncedFilters.maxPrice,
      areas: debouncedFilters.areas.filter((area) =>
        cityPreviewConfig.areas.includes(area),
      ),
    }),
    [cityPreviewConfig.areas, debouncedFilters, sourcePriceRange],
  );

  const filteredHotels = useMemo(() => {
    const items = filterHotelResultsV2(rawHotels, appliedFilters, {
      featuredOnly: debouncedFeaturedOnly,
    });

    return [...items].sort((a, b) => {
      if (a.isAvailable === false && b.isAvailable !== false) return 1;
      if (a.isAvailable !== false && b.isAvailable === false) return -1;
      if (sort === "price_asc")
        return compareOptionalNumber(a.pricePerNight, b.pricePerNight, "asc");
      if (sort === "price_desc")
        return compareOptionalNumber(a.pricePerNight, b.pricePerNight, "desc");
      if (sort === "score_desc")
        return compareOptionalNumber(a.score, b.score, "desc");
      if (sort === "stars_desc") return b.stars - a.stars;

      return (
        (b.score ?? 0) * 0.5 +
        (b.isFeatured ? 1 : 0) +
        (b.originalPrice ? 0.5 : 0) -
        ((a.score ?? 0) * 0.5 +
          (a.isFeatured ? 1 : 0) +
          (a.originalPrice ? 0.5 : 0))
      );
    });
  }, [appliedFilters, debouncedFeaturedOnly, rawHotels, sort]);

  const filterCounts = useMemo(() => {
    const countWith = (partial: Partial<HotelResultsV2Filters>) =>
      filterHotelResultsV2(
        sourceHotels,
        { ...activeFilters, ...partial },
        { featuredOnly },
      ).length;
    const baseAmenityFilters = { ...activeFilters, amenities: [] };
    const baseOfferFilters = { ...activeFilters, offers: [] };
    const currentAmenityCount = countWith({
      amenities: activeFilters.amenities,
    });
    const currentOfferCount = countWith({
      offers: activeFilters.offers,
    });
    const amenityCounts = Object.fromEntries(
      amenityOptions.map((amenity) => [amenity.key, 0]),
    ) as Record<string, number>;
    const offerCounts = Object.fromEntries(
      offerOptions.map((offer) => [offer.key, 0]),
    ) as Record<string, number>;

    sourceHotels.forEach((hotel) => {
      if (
        hotelMatchesHotelResultsV2(hotel, baseAmenityFilters, {
          featuredOnly,
        }) &&
        hasAllAmenityKeys(hotel, activeFilters.amenities)
      ) {
        hotel.amenityItems.forEach((amenity) => {
          if (amenity.key in amenityCounts) {
            amenityCounts[amenity.key] += 1;
          }
        });
      }

      if (
        hotelMatchesHotelResultsV2(hotel, baseOfferFilters, {
          featuredOnly,
        }) &&
        hasAllOfferKeys(hotel, activeFilters.offers)
      ) {
        (hotel.offerItems ?? []).forEach((offer) => {
          if (offer.key in offerCounts) {
            offerCounts[offer.key] += 1;
          }
        });
      }
    });

    activeFilters.amenities.forEach((amenityKey) => {
      if (amenityKey in amenityCounts) {
        amenityCounts[amenityKey] = currentAmenityCount;
      }
    });

    activeFilters.offers.forEach((offerKey) => {
      if (offerKey in offerCounts) {
        offerCounts[offerKey] = currentOfferCount;
      }
    });

    return {
      stars: Object.fromEntries(
        [5, 4, 3, 2].map((star) => [
          star,
          countWith({
            stars: activeFilters.stars.includes(star)
              ? activeFilters.stars
              : [...activeFilters.stars, star],
          }),
        ]),
      ) as Record<number, number>,
      amenities: amenityCounts,
      offers: offerCounts,
      mealPlan: Object.fromEntries(
        ["room_only", "breakfast", "full"].map((meal) => [
          meal,
          countWith({
            mealPlan: activeFilters.mealPlan.includes(meal)
              ? activeFilters.mealPlan
              : [...activeFilters.mealPlan, meal],
          }),
        ]),
      ) as Record<string, number>,
      areas: Object.fromEntries(
        cityPreviewConfig.areas.map((area) => [
          area,
          countWith({
            areas: activeFilters.areas.includes(area)
              ? activeFilters.areas
              : [...activeFilters.areas, area],
          }),
        ]),
      ) as Record<string, number>,
      score: Object.fromEntries(
        [9, 8, 7].map((score) => [score, countWith({ minScore: score })]),
      ) as Record<number, number>,
      services: {
        breakfast: countWith({ breakfast: true }),
        freeCancellation: countWith({ freeCancellation: true }),
      },
      types: {
        isPopular: countWith({ isPopular: true }),
      },
    };
  }, [
    activeFilters,
    amenityOptions,
    offerOptions,
    cityPreviewConfig.areas,
    featuredOnly,
    sourceHotels,
  ]);

  const hasActiveFilters =
    hasHotelResultsV2ActiveFilters(filters) || featuredOnly;
  const activeFilterCount = getHotelResultsV2ActiveFilterCount(
    filters,
    featuredOnly,
  );
  const hasNoAvailableHotels =
    Boolean(hotelResultsResponse?.succeed) &&
    hotelResultsResponse?.available_total === 0;
  const hasUnavailableOnlyResults =
    hasNoAvailableHotels && rawHotels.length > 0;
  const filterSignature = getFiltersSignature(filters, featuredOnly);
  const appliedFilterSignature = getFiltersSignature(
    debouncedFilters,
    debouncedFeaturedOnly,
  );
  const isFilterPending = filterSignature !== appliedFilterSignature;
  const isInitialApiLoading = isApiLoading && !hotelResultsResponse;
  const showInteractionSkeleton = isLoading || isFilterPending;
  const showResultsSkeleton = isInitialApiLoading || showInteractionSkeleton;
  const showLoadingOverlay = isInitialApiLoading;
  const visibleHotels = useMemo(
    () => filteredHotels.slice(0, visibleCount),
    [filteredHotels, visibleCount],
  );
  const hasMoreHotels = visibleCount < filteredHotels.length;

  const currentSort = HOTEL_RESULTS_V2_SORT_OPTIONS.find(
    (option) => option.value === sort,
  );

  const resetVisibleResults = () => setVisibleCount(RESULTS_PAGE_SIZE);

  const updateFilters = (partial: Partial<HotelResultsV2Filters>) => {
    resetVisibleResults();
    setFilters((current) => ({ ...current, ...partial }));
  };

  const updateSort = (value: HotelResultsV2SortValue) => {
    resetVisibleResults();
    setSort(value);
  };

  const resetFilters = () => {
    resetVisibleResults();
    setFilters(initialFilters);
    setFeaturedOnly(false);
    setActiveQuick([]);
  };

  const runMockSearch = () => {
    setIsLoading(true);
    setVisibleCount(RESULTS_PAGE_SIZE);
  };

  const toggleQuick = (key: HotelResultsV2QuickFilterKey) => {
    resetVisibleResults();

    if (key === "featured") {
      setFeaturedOnly((value) => !value);
      setActiveQuick((current) => toggleActiveKey(current, key));
      return;
    }

    const value = Boolean(filters[key]);
    updateFilters({ [key]: !value });
    setActiveQuick((current) => toggleActiveKey(current, key));
  };

  useEffect(() => {
    if (!hasMoreHotels) return;

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        setVisibleCount((current) =>
          Math.min(current + RESULTS_PAGE_SIZE, filteredHotels.length),
        );
      },
      { rootMargin: "420px 0px" },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [filteredHotels.length, hasMoreHotels]);

  useEffect(() => {
    const sidebar = filterSidebarRef.current;
    if (!sidebar) return;

    const updateHeight = () => setFilterSidebarHeight(sidebar.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(sidebar);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const getMaxOffset = () => {
      const viewportHeight = window.innerHeight;
      return Math.max(
        0,
        filterSidebarHeight - (viewportHeight - FILTER_SIDEBAR_STICKY_TOP - 24),
      );
    };

    const updateSidebarOffset = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollYRef.current;

      if (delta !== 0) {
        const maxOffset = getMaxOffset();
        setFilterSidebarOffset((currentOffset) =>
          clampNumber(currentOffset + delta, 0, maxOffset),
        );
      }
      lastScrollYRef.current = currentY;
    };

    lastScrollYRef.current = window.scrollY;
    setFilterSidebarOffset((currentOffset) =>
      clampNumber(currentOffset, 0, getMaxOffset()),
    );
    window.addEventListener("scroll", updateSidebarOffset, { passive: true });
    window.addEventListener("resize", updateSidebarOffset);

    return () => {
      window.removeEventListener("scroll", updateSidebarOffset);
      window.removeEventListener("resize", updateSidebarOffset);
    };
  }, [filterSidebarHeight]);

  useEffect(() => {
    if (!hasAppliedFiltersOnceRef.current) {
      hasAppliedFiltersOnceRef.current = true;
      return;
    }

    const top = resultsTopRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 72) {
      resultsTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [appliedFilters, debouncedFeaturedOnly, sort]);

  useEffect(() => {
    if (!isLoading) return;
    if (isApiLoading) return;

    const timeout = window.setTimeout(() => setIsLoading(false), 250);

    return () => window.clearTimeout(timeout);
  }, [hotelResultsError, hotelResultsResponse, isApiLoading, isLoading]);

  useEffect(() => {
    document.body.classList.toggle(
      "mdsafar-mobile-layer-open",
      mobileFiltersOpen || mobileSearchOpen,
    );

    return () => {
      document.body.classList.remove("mdsafar-mobile-layer-open");
    };
  }, [mobileFiltersOpen, mobileSearchOpen]);

  return (
    <main
      className="min-h-screen bg-[#f2f5f9] pb-20 text-right lg:pb-0"
      dir="rtl"
    >
      <SearchSummary
        onSearch={runMockSearch}
        onMobileSearchOpenChange={setMobileSearchOpen}
      />
      {showLoadingOverlay && (
        <SearchLoadingExperience
          destination={cityName}
          campaignHref={campaignHref}
          campaignLabel={campaign?.title}
        />
      )}
      <ResultsHero cityName={cityName} title={seoTitle} />

      <div className="customContainer mx-auto flex gap-6 py-5 max-md:!px-4">
        <div className="hidden w-[308px] shrink-0 lg:block">
          <div
            className="sticky"
            style={
              {
                "--filter-sidebar-height": `${filterSidebarHeight}px`,
                top: `${FILTER_SIDEBAR_STICKY_TOP}px`,
              } as CSSProperties
            }
          >
            <div
              ref={filterSidebarRef}
              style={{
                transform: `translateY(-${filterSidebarOffset}px)`,
                transition: "transform 80ms linear",
              }}
            >
              <FilterSidebar
                filters={activeFilters}
                counts={filterCounts}
                areas={cityPreviewConfig.areas}
                amenityOptions={amenityOptions}
                offerOptions={offerOptions}
                priceRange={sourcePriceRange}
                priceSamples={sourceHotels
                  .map((hotel) => hotel.pricePerNight)
                  .filter(
                    (price): price is number => typeof price === "number",
                  )}
                onChange={updateFilters}
                onReset={resetFilters}
              />
            </div>
          </div>
        </div>

        <section
          className="min-w-0 flex-1"
          aria-labelledby="hotel-results-v2-list-title"
        >
          <div ref={resultsTopRef} className="scroll-mt-28" />
          <h2 id="hotel-results-v2-list-title" className="sr-only">
            لیست هتل‌های {cityName}
          </h2>
          <div className="-mx-4 mb-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden">
            <QuickFilters
              activeQuickFilters={activeQuick}
              splitAfterFirst
              onToggle={toggleQuick}
            />
            <SortBar
              sort={sort}
              viewMode={viewMode}
              onSortChange={updateSort}
              onViewModeChange={setViewMode}
            />
            <SecondaryQuickFilters
              activeQuickFilters={activeQuick}
              onToggle={toggleQuick}
            />
          </div>

          <div className="mb-5 flex items-center justify-between">
            <p className="whitespace-nowrap text-sm text-slate-950">
              {showResultsSkeleton ? (
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <PhosphorIcon
                    name="Spinner"
                    size={15}
                    className="animate-spin text-primary"
                  />
                  در حال بارگذاری...
                </span>
              ) : filteredHotels.length > 0 ? (
                <>
                  <span className="font-bold">
                    {toHotelResultsV2FaNumber(filteredHotels.length)} هتل
                  </span>{" "}
                  <span className="text-xs text-slate-500">
                    در {cityName} یافت شد
                  </span>
                </>
              ) : (
                <span className="text-xs text-slate-500">
                  نتیجه‌ای برای نمایش وجود ندارد
                </span>
              )}
            </p>

            {currentSort && (
              <span className="hidden items-center gap-1 text-xs text-slate-400 md:inline-flex">
                <PhosphorIcon name={currentSort.icon} size={13} />
                مرتب‌سازی: {currentSort.label}
              </span>
            )}
          </div>

          {hasUnavailableOnlyResults && (
            <UnavailableCityNotice cityName={cityName} />
          )}

          {showResultsSkeleton ? (
            <HotelResultsSkeleton viewMode={viewMode} />
          ) : filteredHotels.length === 0 && !hasUnavailableOnlyResults ? (
            <EmptyResults
              cityName={cityName}
              showCityUnavailableMessage={hasNoAvailableHotels}
              hasActiveFilters={hasActiveFilters}
              onReset={resetFilters}
            />
          ) : viewMode === "grid" ? (
            <>
              <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-3">
                {visibleHotels.map((hotel, index) => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    index={index}
                    viewMode="grid"
                    staySummaryLabel={staySummaryLabel}
                    stayContext={{
                      city: cityCode,
                      checkIn,
                      checkOut,
                      rooms: roomsParam,
                    }}
                  />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {visibleHotels.map((hotel, index) => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    index={index}
                    viewMode="grid"
                    staySummaryLabel={staySummaryLabel}
                    mobileCard
                    stayContext={{
                      city: cityCode,
                      checkIn,
                      checkOut,
                      rooms: roomsParam,
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="hidden space-y-3 md:block">
                {visibleHotels.map((hotel, index) => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    index={index}
                    viewMode="list"
                    staySummaryLabel={staySummaryLabel}
                    stayContext={{
                      city: cityCode,
                      checkIn,
                      checkOut,
                      rooms: roomsParam,
                    }}
                  />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {visibleHotels.map((hotel, index) => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    index={index}
                    viewMode="grid"
                    staySummaryLabel={staySummaryLabel}
                    mobileCard
                    stayContext={{
                      city: cityCode,
                      checkIn,
                      checkOut,
                      rooms: roomsParam,
                    }}
                  />
                ))}
              </div>
            </>
          )}

          {!showResultsSkeleton && hasMoreHotels && (
            <div
              ref={loadMoreRef}
              className="flex justify-center py-6 text-xs text-slate-400"
            >
              <span className="inline-flex items-center gap-2">
                <PhosphorIcon
                  name="Spinner"
                  size={15}
                  className="animate-spin text-primary"
                />
                در حال نمایش هتل‌های بیشتر
              </span>
            </div>
          )}

          {!showResultsSkeleton &&
            !hasMoreHotels &&
            filteredHotels.length > 0 && <ResultsEndMark cityName={cityName} />}
        </section>
      </div>

      <ResultsSeoSection
        cityCode={cityCode}
        cityName={cityName}
        config={cityPreviewConfig}
        seoHtml={seoHtml}
      />

      {!mobileSearchOpen && (
        <MobileBottomBar
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setMobileFiltersOpen(true)}
        />
      )}

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[300] bg-white lg:hidden">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-4">
              <p className="text-base font-bold text-slate-950">فیلترها</p>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="grid size-9 place-items-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                aria-label="بستن فیلترها"
                type="button"
              >
                <PhosphorIcon name="X" size={17} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <FilterSidebar
                filters={activeFilters}
                counts={filterCounts}
                areas={cityPreviewConfig.areas}
                amenityOptions={amenityOptions}
                offerOptions={offerOptions}
                priceRange={sourcePriceRange}
                priceSamples={sourceHotels
                  .map((hotel) => hotel.pricePerNight)
                  .filter(
                    (price): price is number => typeof price === "number",
                  )}
                onChange={updateFilters}
                onReset={resetFilters}
                mobile
              />
            </div>
            {hasActiveFilters && (
              <div className="border-t border-border bg-white p-4">
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-primary py-3 text-sm font-bold text-white shadow-[0_8px_20px_rgba(18,58,90,0.22)]"
                  type="button"
                >
                  اعمال فیلترها
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                    {toHotelResultsV2FaNumber(filteredHotels.length)} مورد
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

function HotelResultsSkeleton({
  viewMode,
}: {
  viewMode: HotelResultsV2ViewMode;
}) {
  const items = Array.from({ length: viewMode === "grid" ? 6 : 4 });

  if (viewMode === "grid") {
    return (
      <>
        <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-3">
          {items.map((_, index) => (
            <HotelSkeletonCard key={index} viewMode="grid" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {items.slice(0, 3).map((_, index) => (
            <HotelSkeletonCard key={index} viewMode="grid" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hidden space-y-3 md:block">
        {items.map((_, index) => (
          <HotelSkeletonCard key={index} viewMode="list" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {items.slice(0, 3).map((_, index) => (
          <HotelSkeletonCard key={index} viewMode="grid" />
        ))}
      </div>
    </>
  );
}

function HotelSkeletonCard({ viewMode }: { viewMode: HotelResultsV2ViewMode }) {
  const isList = viewMode === "list";

  return (
    <div
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
        isList ? "md:flex md:min-h-[238px]" : ""
      }`}
    >
      <div
        className={`animate-pulse bg-slate-200 ${
          isList ? "h-56 md:h-auto md:w-[38%]" : "h-56"
        }`}
      />
      <div className="min-w-0 flex-1 space-y-4 p-5">
        <div className="space-y-2">
          <div className="h-5 w-2/5 animate-pulse rounded bg-slate-200" />
          <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
          <div className="h-4 w-3/5 animate-pulse rounded bg-slate-100" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-5 w-20 animate-pulse rounded-full bg-slate-100"
            />
          ))}
        </div>
        <div className="border-t border-slate-100 pt-4">
          <div className="mb-3 h-5 w-36 animate-pulse rounded bg-slate-200" />
          <div className="flex gap-2">
            <div className="h-10 flex-1 animate-pulse rounded-lg bg-slate-100" />
            <div className="h-10 flex-1 animate-pulse rounded-lg bg-slate-200" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileBottomBar({
  hasActiveFilters,
  activeFilterCount,
  onOpenFilters,
}: {
  hasActiveFilters: boolean;
  activeFilterCount: number;
  onOpenFilters: () => void;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex gap-3 border-t border-border bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] lg:hidden">
      <button
        onClick={onOpenFilters}
        className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-primary bg-white py-3 text-sm font-bold text-primary shadow-[0_-2px_12px_rgba(18,58,90,0.05)] transition hover:bg-primary/5"
        type="button"
      >
        <PhosphorIcon name="SlidersHorizontal" size={16} />
        {hasActiveFilters
          ? `${toHotelResultsV2FaNumber(activeFilterCount)} فیلتر فعال`
          : "فیلترها"}
        {hasActiveFilters && (
          <span className="grid size-5 place-items-center rounded-full bg-primary/10 text-xs font-extrabold text-primary">
            {toHotelResultsV2FaNumber(activeFilterCount)}
          </span>
        )}
      </button>
    </div>
  );
}

function ResultsSeoSection({
  cityCode,
  cityName,
  config,
  seoHtml,
}: {
  cityCode: string;
  cityName: string;
  config: CityPreviewConfig;
  seoHtml?: TrustedHTML;
}) {
  const [expanded, setExpanded] = useState(false);
  const hotelRatingLinks = getHotelRatingLinks(cityCode, cityName);

  return (
    <section
      className="border-t border-border bg-white"
      dir="rtl"
      aria-labelledby="hotel-results-v2-guide-title"
    >
      <div className="customContainer mx-auto py-8 max-md:!px-4">
        <h2
          id="hotel-results-v2-guide-title"
          className="mb-4 text-[16px] font-bold text-[#1a1a2e]"
        >
          راهنمای رزرو هتل در {cityName}
        </h2>
        {seoHtml ? (
          <HTMLContent
            html={seoHtml}
            className="text-[14px] leading-7 text-[#4b5563]"
            readMore
          />
        ) : (
          <>
            <div
              className={`relative overflow-hidden transition-all duration-500 ${
                expanded ? "" : "max-h-24"
              }`}
            >
              <div className="space-y-4 text-[14px] leading-7 text-[#4b5563]">
                <p>{config.seoIntro}</p>
                <p>
                  <strong className="font-bold text-[#1a1a2e]">
                    بهترین مناطق برای اقامت در {cityName}:
                  </strong>{" "}
                  {config.areas.join("، ")} از محدوده‌های پرکاربرد برای انتخاب
                  هتل هستند و بسته به هدف سفر، دسترسی و بودجه می‌توانند
                  گزینه‌های متفاوتی پیش روی مسافر بگذارند.
                </p>
                <p>
                  <strong className="font-bold text-[#1a1a2e]">
                    نکات مهم برای رزرو هتل {cityName}:
                  </strong>{" "}
                  {config.bookingTip}
                </p>
                <p>
                  <strong className="font-bold text-[#1a1a2e]">
                    هزینه اقامت در هتل‌های {cityName}:
                  </strong>{" "}
                  {config.priceTip}
                </p>
              </div>
              {!expanded && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-white/0" />
              )}
            </div>
            <button
              onClick={() => setExpanded((value) => !value)}
              className="mt-4 flex items-center gap-1 text-[12px] font-semibold text-primary transition hover:underline"
              type="button"
            >
              {expanded ? "بستن ↑" : "مطالعه بیشتر ↓"}
            </button>
          </>
        )}

        <nav
          className="mt-7 border-t border-slate-100 pt-5"
          aria-label={`لینک‌های رزرو هتل ${cityName} بر اساس ستاره`}
        >
          <h3 className="mb-3 text-[14px] font-bold text-[#1a1a2e]">
            رزرو هتل {cityName} بر اساس ستاره
          </h3>
          <ul className="flex flex-wrap gap-2">
            {hotelRatingLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] font-medium text-slate-700 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}

function filterHotelResultsV2(
  hotels: HotelResultsV2Item[],
  filters: HotelResultsV2Filters,
  options: { featuredOnly: boolean },
) {
  return hotels.filter((hotel) =>
    hotelMatchesHotelResultsV2(hotel, filters, options),
  );
}

function hotelMatchesHotelResultsV2(
  hotel: HotelResultsV2Item,
  filters: HotelResultsV2Filters,
  options: { featuredOnly: boolean },
) {
  if (options.featuredOnly && !hotel.isFeatured) return false;
  if (filters.hotelName && !hotel.name.includes(filters.hotelName)) {
    return false;
  }
  if (
    typeof hotel.pricePerNight === "number" &&
    hotel.pricePerNight < filters.minPrice
  )
    return false;
  if (
    typeof hotel.pricePerNight === "number" &&
    hotel.pricePerNight > filters.maxPrice
  )
    return false;
  if (filters.stars.length && !filters.stars.includes(hotel.stars)) {
    return false;
  }
  if (filters.breakfast && !hotel.hasBreakfast) return false;
  if (filters.freeCancellation && !hotel.freeCancellation) return false;
  if (!hasAllAmenityKeys(hotel, filters.amenities)) return false;
  if (!hasAllOfferKeys(hotel, filters.offers)) return false;
  if (filters.minScore > 0 && (!hotel.score || hotel.score < filters.minScore))
    return false;
  if (filters.isPopular && !hotel.isPopular) return false;
  if (filters.mealPlan.length > 0) {
    const matchesMealPlan = filters.mealPlan.some((meal) =>
      hotel.mealPlans.includes(meal),
    );

    if (!matchesMealPlan) return false;
  }
  if (
    filters.areas.length > 0 &&
    !filters.areas.some((area) => hotel.location.includes(area))
  ) {
    return false;
  }
  return true;
}

function hasAllAmenityKeys(hotel: HotelResultsV2Item, amenityKeys: string[]) {
  if (amenityKeys.length === 0) return true;

  const hotelAmenityKeys = new Set(
    hotel.amenityItems.map((amenity) => amenity.key),
  );

  return amenityKeys.every((amenityKey) => hotelAmenityKeys.has(amenityKey));
}

function hasAllOfferKeys(hotel: HotelResultsV2Item, offerKeys: string[]) {
  if (offerKeys.length === 0) return true;

  const hotelOfferKeys = new Set(
    (hotel.offerItems ?? []).map((offer) => offer.key),
  );

  return offerKeys.every((offerKey) => hotelOfferKeys.has(offerKey));
}

function hasHotelResultsV2ActiveFilters(filters: HotelResultsV2Filters) {
  return (
    filters.hotelName !== "" ||
    filters.minPrice !== HOTEL_RESULTS_V2_DEFAULT_FILTERS.minPrice ||
    filters.maxPrice !== HOTEL_RESULTS_V2_DEFAULT_FILTERS.maxPrice ||
    filters.stars.length > 0 ||
    filters.breakfast ||
    filters.freeCancellation ||
    filters.pool ||
    filters.parking ||
    filters.wifi ||
    filters.restaurant ||
    filters.gym ||
    filters.spa ||
    filters.amenities.length > 0 ||
    filters.offers.length > 0 ||
    filters.minScore > 0 ||
    filters.isPopular ||
    filters.mealPlan.length > 0 ||
    filters.areas.length > 0
  );
}

function getHotelResultsV2ActiveFilterCount(
  filters: HotelResultsV2Filters,
  featuredOnly: boolean,
) {
  let count = 0;

  if (filters.hotelName.trim() !== "") count += 1;
  if (
    filters.minPrice !== HOTEL_RESULTS_V2_DEFAULT_FILTERS.minPrice ||
    filters.maxPrice !== HOTEL_RESULTS_V2_DEFAULT_FILTERS.maxPrice
  ) {
    count += 1;
  }

  count += filters.stars.length;
  count += filters.amenities.length;
  count += filters.offers.length;
  count += filters.mealPlan.length;
  count += filters.areas.length;

  if (filters.breakfast) count += 1;
  if (filters.freeCancellation) count += 1;
  if (filters.pool) count += 1;
  if (filters.parking) count += 1;
  if (filters.wifi) count += 1;
  if (filters.restaurant) count += 1;
  if (filters.gym) count += 1;
  if (filters.spa) count += 1;
  if (filters.minScore > 0) count += 1;
  if (filters.isPopular) count += 1;
  if (featuredOnly) count += 1;

  return count;
}

function getAmenityOptions(
  hotels: HotelResultsV2Item[],
): HotelResultsV2AmenityItem[] {
  const counts = new Map<
    string,
    { item: HotelResultsV2AmenityItem; count: number }
  >();

  hotels.forEach((hotel) => {
    hotel.amenityItems.forEach((amenity) => {
      const existing = counts.get(amenity.key);
      counts.set(amenity.key, {
        item: existing?.item ?? amenity,
        count: (existing?.count ?? 0) + 1,
      });
    });
  });

  return Array.from(counts.values())
    .sort((first, second) => {
      const importantDiff =
        Number(Boolean(second.item.isImportant)) -
        Number(Boolean(first.item.isImportant));

      if (importantDiff !== 0) return importantDiff;
      if (second.count !== first.count) return second.count - first.count;

      return first.item.label.localeCompare(second.item.label, "fa");
    })
    .map(({ item }) => item);
}

function getOfferOptions(
  hotels: HotelResultsV2Item[],
): HotelResultsV2OfferItem[] {
  const counts = new Map<
    string,
    { item: HotelResultsV2OfferItem; count: number }
  >();

  hotels.forEach((hotel) => {
    (hotel.offerItems ?? []).forEach((offer) => {
      const existing = counts.get(offer.key);
      counts.set(offer.key, {
        item: existing?.item ?? offer,
        count: (existing?.count ?? 0) + 1,
      });
    });
  });

  return Array.from(counts.values())
    .sort((first, second) => {
      if (second.count !== first.count) return second.count - first.count;

      return first.item.label.localeCompare(second.item.label, "fa");
    })
    .map(({ item }) => item);
}

function toggleActiveKey(current: string[], key: string) {
  return current.includes(key)
    ? current.filter((item) => item !== key)
    : [...current, key];
}

function compareOptionalNumber(
  first: number | undefined,
  second: number | undefined,
  direction: "asc" | "desc",
) {
  const firstValue = typeof first === "number" ? first : null;
  const secondValue = typeof second === "number" ? second : null;

  if (firstValue === null && secondValue === null) return 0;
  if (firstValue === null) return 1;
  if (secondValue === null) return -1;

  return direction === "asc"
    ? firstValue - secondValue
    : secondValue - firstValue;
}

function isValidHotelStar(star?: number): star is number {
  return (
    typeof star === "number" && Number.isInteger(star) && star >= 1 && star <= 5
  );
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function useDebouncedValue<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);

    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
}

function getFiltersSignature(
  filters: HotelResultsV2Filters,
  featuredOnly: boolean,
) {
  return JSON.stringify({
    ...filters,
    featuredOnly,
  });
}

type CityPreviewConfig = {
  areas: string[];
  seoIntro: string;
  bookingTip: string;
  priceTip: string;
};

type HotelResultsV2ApiResponse = {
  succeed: boolean;
  message?: string;
  results: HotelResultsV2Item[];
  available_total?: number;
  raw_total?: number;
};

async function fetchHotelResultsV2(
  url: string,
): Promise<HotelResultsV2ApiResponse> {
  const response = await fetch(url, { cache: "no-store" });

  if (!response.ok) {
    return {
      succeed: false,
      message: `${response.status} ${response.statusText}`,
      results: [],
    };
  }

  return response.json();
}

function getCityPreviewConfig(
  cityCode: string,
  cityName: string,
): CityPreviewConfig {
  const normalizedCode = cityCode.toUpperCase();
  const content =
    CITY_PREVIEW_CONTENT[normalizedCode] ??
    createFallbackCityPreviewContent(cityName);

  return content;
}

function getHotelRatingLinks(cityCode: string, cityName: string) {
  return [5, 4, 3, 2, 1].map((star) => ({
    href: `/hotel/${cityCode}/${star}Star`,
    label: `هتل های ${toHotelResultsV2FaNumber(star)} ستاره ${cityName}`,
  }));
}

const CITY_PREVIEW_CONTENT: Record<string, CityPreviewConfig> = {
  KIH: {
    areas: [
      "میدان پردیس",
      "میدان ساحل",
      "جاده جهان",
      "اسکله تفریحی",
      "بلوار موج",
      "مراکز خرید",
      "ساحل اختصاصی",
      "بازار پردیس",
    ],
    seoIntro:
      "کیش به خاطر ساحل‌های آرام، مراکز خرید و تفریحات دریایی یکی از محبوب‌ترین مقصدهای سفر جنوب ایران است. انتخاب هتل مناسب در کیش معمولاً به نزدیکی به ساحل، مراکز خرید، اسکله تفریحی و سطح امکانات هتل بستگی دارد.",
    bookingTip:
      "در فصل‌های شلوغ مثل نوروز، تعطیلات رسمی و ماه‌های خنک سال، ظرفیت هتل‌های کیش سریع‌تر تکمیل می‌شود. هنگام رزرو، فاصله هتل تا ساحل، بازارها، اسکله تفریحی و خدماتی مثل صبحانه و ترانسفر را بررسی کنید.",
    priceTip:
      "قیمت هتل‌های کیش بسته به نزدیکی به ساحل، ستاره هتل و فصل سفر تغییر زیادی دارد. هتل‌های اقتصادی برای سفرهای خرید مناسب‌اند و هتل‌های ۴ و ۵ ستاره معمولاً امکانات تفریحی و ساحلی کامل‌تری ارائه می‌کنند.",
  },
  MHD: {
    areas: [
      "حرم مطهر",
      "خیابان امام رضا",
      "بلوار سجاد",
      "بلوار توس",
      "کوهسنگی",
      "ملک‌آباد",
      "الهیه",
      "فرامرز عباسی",
    ],
    seoIntro:
      "مشهد یکی از پرترددترین شهرهای زیارتی ایران است و تنوع هتل‌ها در آن از اقامتگاه‌های اقتصادی تا هتل‌های لوکس نزدیک حرم را شامل می‌شود.",
    bookingTip:
      "در مناسبت‌های مذهبی، نوروز و تابستان بهتر است هتل‌های مشهد را زودتر رزرو کنید و فاصله تا حرم، دسترسی به حمل‌ونقل و امکانات اتاق را بررسی کنید.",
    priceTip:
      "قیمت هتل‌های مشهد به فاصله از حرم، ستاره هتل، وعده غذایی و زمان سفر وابسته است و در ایام پرتردد افزایش پیدا می‌کند.",
  },
  SYZ: {
    areas: [
      "دروازه قرآن",
      "بازار وکیل",
      "خیابان زند",
      "معالی‌آباد",
      "قصرالدشت",
      "نزدیک حافظیه",
    ],
    seoIntro:
      "شیراز با جاذبه‌های تاریخی، باغ‌ها و فضای فرهنگی خود مقصدی محبوب برای سفرهای تفریحی و خانوادگی است.",
    bookingTip:
      "برای رزرو هتل در شیراز، نزدیکی به بافت تاریخی، بازار وکیل، حافظیه یا مسیرهای دسترسی به باغ‌ها می‌تواند انتخاب شما را بهتر کند.",
    priceTip:
      "قیمت هتل‌های شیراز بر اساس موقعیت، فصل سفر و سطح امکانات متفاوت است و در تعطیلات نوروز معمولاً تقاضا بیشتر می‌شود.",
  },
  GSM: {
    areas: [
      "میدان حافظ",
      "جاده ساحلی جنوبی",
      "نزدیک بازار قدیم",
      "درگهان",
      "ساحل زیتون",
      "مرکز قشم",
    ],
    seoIntro:
      "قشم مقصدی مناسب برای خرید، طبیعت‌گردی و سفرهای ساحلی است و انتخاب هتل در آن به نزدیکی به بازارها یا ساحل بستگی دارد.",
    bookingTip:
      "اگر هدف سفر خرید است، نزدیکی به بازارها مهم‌تر است؛ برای سفر تفریحی، هتل‌های نزدیک ساحل و مسیرهای گردشگری انتخاب بهتری هستند.",
    priceTip:
      "قیمت هتل‌های قشم معمولاً در فصل‌های خنک سال و تعطیلات افزایش پیدا می‌کند و هتل‌های نزدیک بازار یا ساحل تقاضای بیشتری دارند.",
  },
};

function createFallbackCityPreviewContent(cityName: string): CityPreviewConfig {
  return {
    areas: ["مرکز شهر", "بلوار ساحلی", "محدوده گردشگری", "نزدیک مراکز خرید"],
    seoIntro: `${cityName} یکی از مقصدهای پرتقاضا برای رزرو هتل است و انتخاب محل اقامت در آن به هدف سفر، بودجه و امکانات مورد انتظار بستگی دارد.`,
    bookingTip:
      "قبل از رزرو، موقعیت هتل، دسترسی به مراکز مهم شهر، قوانین کنسلی و امکانات اتاق را بررسی کنید.",
    priceTip:
      "قیمت هتل‌ها با توجه به فصل سفر، موقعیت، ستاره هتل و ظرفیت اتاق‌ها تغییر می‌کند.",
  };
}

function EmptyResults({
  cityName,
  showCityUnavailableMessage,
  hasActiveFilters,
  onReset,
}: {
  cityName: string;
  showCityUnavailableMessage: boolean;
  hasActiveFilters: boolean;
  onReset: () => void;
}) {
  const title = showCityUnavailableMessage
    ? `برای شهر ${cityName} هتلی یافت نشد.`
    : hasActiveFilters
      ? "هتلی با این فیلترها پیدا نشد"
      : `برای ${cityName} نتیجه‌ای برای نمایش وجود ندارد.`;
  const description = showCityUnavailableMessage
    ? "تاریخ سفر را تغییر دهید یا چند دقیقه بعد دوباره جستجو کنید."
    : hasActiveFilters
      ? "فیلترها را کاهش دهید یا شرایط جستجو را تغییر دهید."
      : "تاریخ سفر را تغییر دهید یا دوباره جستجو کنید.";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center">
      <p className="text-base font-bold text-slate-950">{title}</p>
      <p className="mt-1 text-sm text-slate-500">{description}</p>
      {hasActiveFilters && (
        <button
          onClick={onReset}
          className="mt-4 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white"
          type="button"
        >
          حذف همه فیلترها
        </button>
      )}
    </div>
  );
}

function UnavailableCityNotice({ cityName }: { cityName: string }) {
  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-right shadow-[0_8px_22px_rgba(245,158,11,0.08)]">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700">
          <PhosphorIcon name="WarningCircle" size={18} weight="fill" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-950">
            برای شهر {cityName} هتلی یافت نشد.
          </p>
          <p className="mt-1 text-xs leading-6 text-slate-600">
            هتل‌های زیر برای آشنایی با گزینه‌های این شهر نمایش داده شده‌اند، اما
            ظرفیت آن‌ها برای تاریخ انتخابی شما تکمیل است.
          </p>
        </div>
      </div>
    </div>
  );
}

function ResultsEndMark({ cityName }: { cityName: string }) {
  return (
    <div className="py-8 text-center">
      <span className="inline-flex items-center gap-2 text-xs text-slate-400">
        <span className="h-px w-8 bg-slate-200" />
        پایان نتایج برای {cityName}
        <span className="h-px w-8 bg-slate-200" />
      </span>
    </div>
  );
}

function getNightsFromSearchParams(
  checkIn?: string | null,
  checkOut?: string | null,
) {
  if (!checkIn || !checkOut) return 3;

  const start = parseHotelResultsV2Date(checkIn, startOfToday());
  const end = parseHotelResultsV2Date(checkOut, addDays(start, 1));
  const diff = Math.round((end.getTime() - start.getTime()) / 86_400_000);

  return Number.isFinite(diff) && diff > 0 ? diff : 3;
}

function getDefaultCheckIn() {
  return jFormat(startOfToday(), "yyyy-MM-dd");
}

function getDefaultCheckOut() {
  return jFormat(addDays(startOfToday(), 1), "yyyy-MM-dd");
}

function getOccupancySummaryFromRoomsParam(value?: string | null) {
  const rooms = (value || "2")
    .split("_")
    .map((roomValue) => {
      const parts = roomValue.split("-").filter(Boolean);
      const adults = Number(parts[0]);

      return {
        adults: Number.isFinite(adults) && adults > 0 ? adults : 1,
        children: Math.max(0, parts.length - 1),
      };
    })
    .filter((room) => room.adults > 0);
  const safeRooms = rooms.length ? rooms : [{ adults: 2, children: 0 }];
  const guests = safeRooms.reduce(
    (sum, room) => sum + room.adults + room.children,
    0,
  );

  return {
    guests,
    rooms: safeRooms.length,
    label: `${guests.toLocaleString("fa-IR")} نفر · ${safeRooms.length.toLocaleString("fa-IR")} اتاق`,
  };
}

function getStaySummaryLabel(nights: number, guests: number) {
  return `${toHotelResultsV2FaNumber(nights)} شب، ${toHotelResultsV2FaNumber(guests)} نفر`;
}
